"use client"

import Image from "next/image"
import { memo, ReactEventHandler, useState } from "react"
import styled from "styled-components"

const StyledImageContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
`

const getSizes = (size: string[] | string | null | undefined): string => {
  if (!size) return "100vw"
  if (typeof size === "string") return size
  if (Array.isArray(size)) {
    const [mobile, desktop] = size
    if (mobile && desktop) {
      return `(min-width: 768px) ${desktop}, ${mobile}`
    }
    return mobile || desktop || "100vw"
  }
  return "100vw"
}

type NextImageProps = {
  image: { imageUrl: string; alt: string; icon?: string; text?: string }
  imageSizes?: string[] | string
  cover?: boolean
  priority?: boolean
  onLoadCallBack?: ReactEventHandler<HTMLImageElement>
  containerClassName?: string
  onLoadingComplete?: () => void
  loadingEager?: boolean
  unoptimized?: boolean
  noLazyBoundry?: boolean
  role?: string
  ariaHidden?: boolean
  objectPosition?:
    | "center"
    | "top"
    | "bottom"
    | "left"
    | "right"
    | "top left"
    | "top right"
    | "bottom left"
    | "bottom right"
    | "center left"
    | "center right"
    | "center top"
    | "center bottom"
} & React.HTMLProps<HTMLImageElement>

const NextImage = ({
  image,
  imageSizes,
  cover = true,
  className,
  containerClassName,
  priority = false,
  onLoadCallBack,
  onLoadingComplete,
  loadingEager = false,
  unoptimized,
  role,
  ariaHidden,
  objectPosition = "center",
}: NextImageProps) => {
  const [useUnoptimized, setUseUnoptimized] = useState(unoptimized || false)

  const handleError = () => {
    console.error(
      "Next.js Image Optimization failed for:",
      image?.imageUrl,
      "- Falling back to unoptimized mode"
    )
    // If not already unoptimized, try again with unoptimized flag
    if (!useUnoptimized) {
      setUseUnoptimized(true)
    }
  }

  return (
    <StyledImageContainer className={containerClassName}>
      <Image
        src={image?.imageUrl}
        alt={image?.alt || ""}
        className={className}
        fill
        style={{
          objectFit: cover ? "cover" : "contain",
          objectPosition,
        }}
        priority={priority}
        onLoad={onLoadCallBack}
        onLoadingComplete={onLoadingComplete}
        onError={handleError}
        loading={loadingEager || priority ? "eager" : "lazy"}
        unoptimized={useUnoptimized}
        role={role}
        aria-hidden={ariaHidden}
        sizes={getSizes(imageSizes)}
      />
    </StyledImageContainer>
  )
}

export default memo(NextImage)
